import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  FiSearch, FiX, FiXCircle, FiFolder, FiFileText, FiFile, FiImage,
  FiChevronRight, FiChevronDown, FiArrowLeft, FiArrowRight,
  FiInfo, FiRefreshCw, FiPaperclip, FiCheck,
} from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';

/* ── env / auth (same conventions as the other AMS browsers in this repo) ── */
const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken = () => localStorage.getItem('amsToken') || '';

const FONT = 'Poppins, sans-serif';

/* Visually hidden but announced (inline so it holds wherever this renders). */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

/* ── types ── */
interface AmsFileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  lastModified?: string;
  children?: AmsFileItem[];
  downloadUrl?: string;
  parentFolderId?: string;
}

interface NowCertsClient {
  name: string;
  databaseId: string;
  eMail?: string;
}

/** One folder's worth of selected AMS files — the upload API lists a single
 *  folder and filters by file ids, so multi-folder selections are grouped. */
export interface AmsUploadSelection {
  insuredId: string;
  folderId: string;
  amsFileIds: string[];
}

interface SelectFromAmsDialogProps {
  visible: boolean;
  onClose: () => void;
  /** Receives the selected AMS file ids grouped per folder. Should upload them
   *  server-side (e.g. into the task's kanban transaction) and throw on failure. */
  onUpload: (selections: AmsUploadSelection[]) => Promise<void>;
  title?: string;
}

/* ── helpers ── */
const mapFiles = (rawFiles: any[], parentId: string): AmsFileItem[] =>
  (rawFiles || []).map((f: any) => ({
    id: f.databaseId,
    name: f.name,
    type: f.fileOrFolder === 'Folder' ? 'folder' : 'file',
    description: f.fileOrFolder,
    size: f.size?.toString() || '-',
    uploader: f.creatorName || '-',
    created: f.createDate || '-',
    lastModified: f.changeDate || '-',
    downloadUrl: f.downloadUrl,
    children: f.fileOrFolder === 'Folder' ? [] : undefined,
    parentFolderId: f.parentId || parentId,
  }));

const fmtSize = (size?: string) => {
  if (!size || size === '-') return '—';
  const n = parseInt(size, 10);
  if (isNaN(n) || n === 0) return '—';
  return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
};

const fileIconFor = (item: AmsFileItem): { Icon: React.ComponentType<any>; color: string } => {
  if (item.type === 'folder') return { Icon: FiFolder, color: '#ec4899' };
  const ext = item.name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return { Icon: FiFileText, color: '#dc2626' };
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return { Icon: FiImage, color: '#2563eb' };
  if (['doc', 'docx'].includes(ext || '')) return { Icon: FiFileText, color: '#1d4ed8' };
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) return { Icon: FiFileText, color: '#16a34a' };
  return { Icon: FiFile, color: '#6b7280' };
};

/* ════════════════════════════════════════════════
   SelectFromAmsDialog — search a client, browse their
   AMS Drive, multi-select files and upload them to the task.
   ════════════════════════════════════════════════ */
const SelectFromAmsDialog: React.FC<SelectFromAmsDialogProps> = ({
  visible,
  onClose,
  onUpload,
  title = 'Select from AMS Drive',
}) => {
  const { theme } = useTheme();

  /* client search */
  const [clientQuery, setClientQuery] = React.useState('');
  const [clientList, setClientList] = React.useState<NowCertsClient[]>([]);
  const [clientLoading, setClientLoading] = React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState<NowCertsClient | null>(null);

  /* file browser */
  const [rootFolder, setRootFolder] = React.useState<AmsFileItem | null>(null);
  const [currentFolder, setCurrentFolder] = React.useState<AmsFileItem | null>(null);
  const [tableData, setTableData] = React.useState<AmsFileItem[]>([]);
  const [tableLoading, setTableLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState('');
  const [expandedFolders, setExpandedFolders] = React.useState<Record<string, boolean>>({});
  const [selectedFiles, setSelectedFiles] = React.useState<AmsFileItem[]>([]);
  const [folderHistory, setFolderHistory] = React.useState<AmsFileItem[]>([]);
  const [historyIndex, setHistoryIndex] = React.useState(-1);

  /* upload */
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');

  const border = 'var(--border-glass, rgba(255,255,255,0.09))';
  const surfaceBg = theme === 'dark' ? '#1e1e35' : '#ffffff';
  const subtleBg = theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafafa';
  const hoverBg = theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6';
  const accent = '#a78bfa';

  const resetBrowser = React.useCallback(() => {
    setRootFolder(null);
    setCurrentFolder(null);
    setTableData([]);
    setExpandedFolders({});
    setFolderHistory([]);
    setHistoryIndex(-1);
    setSelectedFiles([]);
    setFetchError('');
  }, []);

  const resetAll = React.useCallback(() => {
    setClientQuery('');
    setClientList([]);
    setSelectedClient(null);
    setUploadError('');
    resetBrowser();
  }, [resetBrowser]);

  /* ── client search (debounced) ── */
  React.useEffect(() => {
    if (!visible || selectedClient) return;
    if (!clientQuery.trim()) { setClientList([]); setClientLoading(false); return; }
    setClientLoading(true);
    const t = setTimeout(() => {
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/customerlist`, {
        params: { searchText: clientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => { setClientList(r.data || []); setClientLoading(false); })
        .catch(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientQuery, selectedClient, visible]);

  /* ── fetch AMS files ── */
  const fetchFiles = React.useCallback(async (insuredId: string, folderId?: string) => {
    const url = `${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${getNcToken()}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, []);

  /* load root files when a client is selected */
  React.useEffect(() => {
    if (!selectedClient || !visible) return;
    resetBrowser();
    setTableLoading(true);
    fetchFiles(selectedClient.databaseId)
      .then(data => {
        const fd = data?.data || data;
        const rootId = fd.currentFolderId || selectedClient.databaseId;
        const root: AmsFileItem = {
          id: rootId,
          name: fd.name || selectedClient.name || 'Root',
          type: 'folder',
          children: mapFiles(fd.files || [], rootId),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
        setTableLoading(false);
      })
      .catch(() => {
        setFetchError('Unable to load AMS files for this client. Please try again.');
        setTableLoading(false);
      });
  }, [selectedClient, visible, fetchFiles, resetBrowser]);

  /* ── folder navigation ── */
  const openFolder = React.useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders(p => ({ ...p, [folder.id]: true }));
    setFolderHistory(prev => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex(f => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (node: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (node.id === targetId) return { ...node, children: newChildren };
    if (node.children) return { ...node, children: node.children.map(c => updateChildrenInTree(c, targetId, newChildren)) };
    return node;
  };

  const toggleFolder = React.useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders(p => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.children && item.children.length === 0 && selectedClient) {
      setTableLoading(true);
      fetchFiles(selectedClient.databaseId, item.id)
        .then(data => {
          const newChildren = mapFiles(data?.data?.files || [], item.id);
          setRootFolder(prev => prev ? updateChildrenInTree(prev, item.id, newChildren) : prev);
          openFolder(item, newChildren);
          setTableLoading(false);
        })
        .catch(() => setTableLoading(false));
    } else if (item.type === 'folder') {
      openFolder(item);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, selectedClient, fetchFiles, openFolder]);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const goHistory = (dir: -1 | 1) => {
    const idx = historyIndex + dir;
    const f = folderHistory[idx];
    if (!f) return;
    setHistoryIndex(idx);
    setCurrentFolder(f);
    setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  /* breadcrumb path */
  const pathSegments = React.useMemo(() => {
    if (!rootFolder) return [] as AmsFileItem[];
    const target = currentFolder?.id || rootFolder.id;
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === target) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(rootFolder, []);
    return path.length ? path : [rootFolder];
  }, [rootFolder, currentFolder]);

  /* ── file multi-select ── */
  const toggleFileSelect = React.useCallback((item: AmsFileItem) => {
    if (item.type !== 'file') return;
    setSelectedFiles(prev =>
      prev.some(f => f.id === item.id) ? prev.filter(f => f.id !== item.id) : [...prev, item]
    );
  }, []);

  /* ── upload: hand the selected file ids to the caller, grouped per folder;
     the backend downloads the files from AMS server-side (the pre-signed S3
     download URLs are not CORS-enabled, so the browser cannot fetch them) ── */
  const handleUploadSelected = async () => {
    if (selectedFiles.length === 0 || uploading || !selectedClient) return;
    setUploadError('');
    setUploading(true);
    try {
      const byFolder = new Map<string, string[]>();
      for (const item of selectedFiles) {
        const folderId = item.parentFolderId || currentFolder?.id || rootFolder?.id || selectedClient.databaseId;
        byFolder.set(folderId, [...(byFolder.get(folderId) || []), item.id]);
      }
      const selections: AmsUploadSelection[] = Array.from(byFolder, ([folderId, amsFileIds]) => ({
        insuredId: selectedClient.databaseId,
        folderId,
        amsFileIds,
      }));
      await onUpload(selections);
      resetAll();
      onClose();
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to upload selected files.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    resetAll();
    onClose();
  };

  /* ── dialog a11y: initial focus, return focus to opener, Escape, Tab trap ── */
  const titleId = React.useId();
  const searchId = React.useId();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);
  const handleCloseRef = React.useRef(handleClose);
  handleCloseRef.current = handleClose;

  React.useEffect(() => {
    if (!visible) return;
    const opener = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const input = searchInputRef.current;
      if (input && !input.disabled) input.focus();
      else closeBtnRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [visible]);

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusables = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  /* ── folder tree (left panel) ── */
  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    const isActive = currentFolder?.id === item.id;
    return (
      <div key={item.id}>
        <button
          type="button"
          className="mc-ams-btn-reset"
          aria-expanded={!!expanded}
          aria-current={isActive ? 'true' : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '6px 8px',
            paddingLeft: level * 16 + 10, cursor: 'pointer', borderRadius: 6,
            background: isActive ? 'rgba(167,139,250,0.12)' : 'transparent',
            color: 'var(--text-primary)', fontSize: 12, fontFamily: FONT,
            width: '100%', border: 'none', textAlign: 'left',
          }}
          onClick={() => toggleFolder(item)}
          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
        >
          {expanded
            ? <FiChevronDown size={11} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.6 }} />
            : <FiChevronRight size={11} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.6 }} />}
          <FiFolder size={13} aria-hidden="true" style={{ flexShrink: 0, color: '#ec4899' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
            {item.name}
          </span>
        </button>
        {expanded && (item.children || [])
          .filter(c => c.type === 'folder')
          .map(c => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  if (!visible) return null;

  const thStyle: React.CSSProperties = {
    position: 'sticky', top: 0, background: subtleBg, textAlign: 'left',
    padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
    fontFamily: FONT, borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap', zIndex: 1,
  };
  const tdStyle: React.CSSProperties = {
    padding: '7px 10px', fontSize: 12, color: 'var(--text-primary)', fontFamily: FONT,
    borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis', maxWidth: 220,
  };

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={handleClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="mc-ams-dialog"
        onKeyDown={handlePanelKeyDown}
        style={{
          width: 'min(1100px, 94vw)', height: '84vh', background: surfaceBg,
          border: `1px solid ${border}`, borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiFolder size={16} aria-hidden="true" style={{ color: accent }} />
            <span id={titleId} style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', fontFamily: FONT }}>{title}</span>
          </div>
          <button
            type="button"
            ref={closeBtnRef}
            onClick={handleClose}
            title="Close"
            aria-label="Close"
            className="mc-a11y-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 4 }}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* body */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '14px 20px' }}>
          {/* client search */}
          <div style={{ maxWidth: 360, flexShrink: 0, marginBottom: 12 }}>
            <label htmlFor={searchId} style={{ fontSize: 12, fontWeight: 600, color: accent, fontFamily: FONT }}>Search client</label>
            <div style={{ position: 'relative', marginTop: 4 }}>
              <FiSearch size={13} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                ref={searchInputRef}
                id={searchId}
                className="mc-a11y-field"
                type="text"
                value={selectedClient ? selectedClient.name : clientQuery}
                placeholder="Enter client name to search..."
                disabled={!!selectedClient}
                onChange={e => setClientQuery(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '7px 32px 7px 30px',
                  fontSize: 12.5, fontFamily: FONT, color: 'var(--text-primary)',
                  background: 'transparent', border: `1px solid ${border}`, borderRadius: 7,
                }}
              />
              {selectedClient && (
                <button
                  type="button"
                  title="Clear selection"
                  aria-label="Clear selected client"
                  className="mc-a11y-btn"
                  onClick={() => { setSelectedClient(null); setClientQuery(''); setClientList([]); resetBrowser(); }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}
                >
                  <FiXCircle size={14} />
                </button>
              )}
              {!selectedClient && clientQuery.trim().length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 2,
                  maxHeight: 250, overflowY: 'auto', background: surfaceBg,
                  border: `1px solid ${border}`, borderRadius: 7, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                }}>
                  {clientLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                      <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
                    </div>
                  ) : clientList.length === 0 ? (
                    <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', fontFamily: FONT }}>
                      No clients found
                    </div>
                  ) : (
                    clientList.map(c => (
                      <button
                        type="button"
                        key={c.databaseId}
                        className="mc-ams-btn-reset"
                        onClick={() => { setSelectedClient(c); setClientQuery(c.name); setClientList([]); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 12px', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', fontFamily: FONT, borderBottom: `1px solid ${border}` }}
                        onMouseEnter={e => (e.currentTarget.style.background = hoverBg)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <span style={{ display: 'block' }}>{c.name || '(Unnamed client)'}</span>
                        {c.eMail && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{c.eMail}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* content area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!selectedClient && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <FiInfo size={18} style={{ color: accent }} />
                  <p style={{ marginTop: 8, fontSize: 13, fontWeight: 500, color: accent, fontFamily: FONT }}>
                    Search client to load files
                  </p>
                </div>
              </div>
            )}

            {selectedClient && tableLoading && !rootFolder && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiRefreshCw size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
              </div>
            )}

            {selectedClient && !tableLoading && fetchError && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontFamily: FONT }}>
                  <FiInfo size={18} style={{ color: accent }} />
                  <p style={{ marginTop: 8, fontSize: 12.5 }}>{fetchError}</p>
                </div>
              </div>
            )}

            {selectedClient && rootFolder && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* selected file chips */}
                {selectedFiles.length > 0 && (
                  <div style={{ flexShrink: 0, marginBottom: 10, border: `1px solid ${border}`, borderRadius: 8, padding: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: accent, fontFamily: FONT }}>
                        Selected files from AMS Drive ({selectedFiles.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles([])}
                        className="mc-a11y-btn"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#f87171', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <FiXCircle size={11} /> Clear all
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selectedFiles.map(f => (
                        <span
                          key={f.id}
                          title={f.name}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 240,
                            padding: '3px 9px', borderRadius: 'var(--radius-xs)', background: 'rgba(139,92,246,0.10)',
                            border: `1px solid ${border}`, fontSize: 11, color: 'var(--text-primary)', fontFamily: FONT,
                          }}
                        >
                          <FiPaperclip size={10} style={{ flexShrink: 0, opacity: 0.75 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <button
                            type="button"
                            onClick={() => toggleFileSelect(f)}
                            aria-label={`Remove ${f.name}`}
                            title={`Remove ${f.name}`}
                            className="mc-a11y-btn"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}
                          >
                            <FiX size={11} aria-hidden="true" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* path bar */}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button
                      type="button" title="Back" aria-label="Back" className="mc-a11y-btn" disabled={!canGoBack} onClick={() => goHistory(-1)}
                      style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: `1px solid ${border}`, borderRadius: 5, color: 'var(--text-secondary)', cursor: canGoBack ? 'pointer' : 'not-allowed', opacity: canGoBack ? 1 : 0.4 }}
                    >
                      <FiArrowLeft size={12} />
                    </button>
                    <button
                      type="button" title="Forward" aria-label="Forward" className="mc-a11y-btn" disabled={!canGoForward} onClick={() => goHistory(1)}
                      style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: `1px solid ${border}`, borderRadius: 5, color: 'var(--text-secondary)', cursor: canGoForward ? 'pointer' : 'not-allowed', opacity: canGoForward ? 1 : 0.4 }}
                    >
                      <FiArrowRight size={12} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: FONT }}>
                    {pathSegments.map((seg, i) => (
                      <React.Fragment key={seg.id}>
                        {i > 0 && <span style={{ opacity: 0.5 }}>/</span>}
                        <span title={seg.name} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{seg.name}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* tree + table */}
                <div style={{ flex: 1, display: 'flex', minHeight: 0, border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ width: 250, flexShrink: 0, overflowY: 'auto', background: subtleBg, borderRight: `1px solid ${border}`, padding: 6 }}>
                    {renderTreeItem(rootFolder)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                    {tableLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <FiRefreshCw size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)' }} />
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...thStyle, width: 30 }}><span style={SR_ONLY}>Select</span></th>
                            <th style={thStyle}>Title</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>Size</th>
                            <th style={thStyle}>Uploader</th>
                            <th style={thStyle}>Created</th>
                            <th style={thStyle}>Last modified</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tableData.length === 0 && (
                            <tr>
                              <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-secondary)', maxWidth: 'none' }}>
                                This folder is empty
                              </td>
                            </tr>
                          )}
                          {tableData.map(item => {
                            const { Icon, color } = fileIconFor(item);
                            const isSelected = selectedFiles.some(f => f.id === item.id);
                            return (
                              <tr
                                key={item.id}
                                onClick={() => item.type === 'folder' ? toggleFolder(item) : toggleFileSelect(item)}
                                style={{ cursor: 'pointer', background: isSelected ? 'rgba(167,139,250,0.12)' : 'transparent' }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = hoverBg; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <td style={{ ...tdStyle, maxWidth: 30 }}>
                                  {item.type === 'file' && (
                                    <button
                                      type="button"
                                      role="checkbox"
                                      aria-checked={isSelected}
                                      aria-label={`Select ${item.name}`}
                                      className="mc-ams-btn-reset"
                                      onClick={e => { e.stopPropagation(); toggleFileSelect(item); }}
                                      style={{
                                        width: 15, height: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: 4, border: `1px solid ${isSelected ? accent : border}`,
                                        background: isSelected ? accent : 'transparent', color: '#fff',
                                        padding: 0, cursor: 'pointer',
                                      }}
                                    >
                                      {isSelected && <FiCheck size={10} aria-hidden="true" />}
                                    </button>
                                  )}
                                </td>
                                <td style={tdStyle}>
                                  {item.type === 'folder' ? (
                                    <button
                                      type="button"
                                      className="mc-ams-btn-reset"
                                      aria-label={`Open folder ${item.name}`}
                                      onClick={e => { e.stopPropagation(); toggleFolder(item); }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                        color: 'inherit', font: 'inherit', textAlign: 'left',
                                      }}
                                    >
                                      <Icon size={13} aria-hidden="true" style={{ color, flexShrink: 0 }} />
                                      <span title={item.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                    </button>
                                  ) : (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                                      <Icon size={13} aria-hidden="true" style={{ color, flexShrink: 0 }} />
                                      <span title={item.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                    </span>
                                  )}
                                </td>
                                <td style={tdStyle}>{item.description || '—'}</td>
                                <td style={tdStyle}>{fmtSize(item.size)}</td>
                                <td style={tdStyle}>{item.uploader || '—'}</td>
                                <td style={tdStyle}>{item.created || '—'}</td>
                                <td style={tdStyle}>{item.lastModified || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, padding: '12px 20px', borderTop: `1px solid ${border}` }}>
          {uploadError && (
            <span role="alert" style={{ fontSize: 11.5, color: '#f87171', fontFamily: FONT, marginRight: 'auto' }}>{uploadError}</span>
          )}
          <button
            type="button"
            onClick={handleUploadSelected}
            className="mc-a11y-btn"
            disabled={uploading || selectedFiles.length === 0}
            style={{
              padding: '8px 22px', borderRadius: 7, border: 'none',
              background: 'linear-gradient(135deg, #6d28d9, #a200ff)', color: '#fff',
              fontSize: 13, fontWeight: 600, fontFamily: FONT,
              cursor: (uploading || selectedFiles.length === 0) ? 'not-allowed' : 'pointer',
              opacity: (uploading || selectedFiles.length === 0) ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 7,
            }}
          >
            {uploading && <FiRefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SelectFromAmsDialog;

